import React, { useEffect, useState, useCallback } from 'react'
import { Button, TextField, IconButton, Tabs, Tab } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ReferralProp, ReferralItem, ReferralStatus } from '../../interfaces/Referral';
import ModalReferralCode from '../../components/specific/modal/ModalReferralCode';
import { useTranslation } from 'react-i18next';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useToast } from '../../hooks/useToast';
import { referralService } from '../../services';
import { useGlobalData } from '../../hooks/globalDataContext';
import { notifyCreditsUpdated } from '../../utils/creditsEvents';
import Loader from '../../assets/gifs/loader.gif'

const Referral = () => {
    const { t } = useTranslation();
    const { success } = useToast();
    const { refreshCredits } = useGlobalData();
    const [activeTab, setActiveTab] = useState(0);
    const [promoCode, setPromoCode] = useState<string>('');
    const [data, setData] = useState<any>({});
    const [referralData, setReferralData] = useState<ReferralProp>({
        pending: [],
        expired: [],
        completed: [],
    });
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [referralActive, setReferralActive] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const getPromoCode = async () => {
            setLoading(true);
            try {
                const data = await referralService.getReferralLink();
                setPromoCode(data.referral_link);
                setData(data);
                setReferralActive(data.referral_active !== false);
            } catch (error) {
                console.error('Errore caricamento codice promo', error);
                setReferralActive(false);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        };

        const getReferralData = async () => {
            setLoading(true);
            try {
                const data = await referralService.getReferralData();
                setReferralData(data);
                // Aggiorna i crediti quando i dati referral cambiano
                refreshCredits(true);
                notifyCreditsUpdated();
            } catch (error) {
                console.error('Errore caricamento dati referral', error);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        }

        getPromoCode();
        getReferralData();
    }, [refreshCredits]);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(promoCode);
        success(t('referral:referral_modal_success'));
    }, [promoCode, success, t]);

    const handleShare = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    }, []);

    const formatDateAndTime = (dateString: string) => {
        const date = new Date(dateString);
        const formattedDate = date.toLocaleDateString('it-IT');
        const formattedTime = date.toLocaleTimeString('it-IT', {
            hour: '2-digit',
            minute: '2-digit'
        });
        return { date: formattedDate, time: formattedTime };
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case ReferralStatus.PENDING:
                return t('referral:referral_pending_text');
            case ReferralStatus.ACCEPTED:
                return t('referral:referral_completed_text');
            case ReferralStatus.REJECTED:
                return t('referral:referral_expired_text');
            case ReferralStatus.ACCEPTED_WITH_NO_CREDS:
                return t('referral:referral_completed_no_creds_text');
            default:
                return t('referral:referral_pending_text');
        }
    };

    const tableData = activeTab === 0 ? (referralData.pending || []) :
        activeTab === 1 ? (referralData.expired || []) :
            (referralData.completed || []);

    const pendingCount = referralData.pending?.length || 0;
    const rejectedCount = referralData.expired?.length || 0;

    const completedCredits = (referralData.completed || []).reduce((acc, item) => acc + (item.status === ReferralStatus.ACCEPTED ? 10 : 0), 0);

    return (
        <div className='w-full h-auto flex flex-col justify-center p-4 gap-5 md:gap-8 md:py-14 md:px-[23%]'>

            {!referralActive ? (
                <div className='w-full flex flex-col items-center justify-center gap-3 p-8 bg-[#fff3f3] rounded-md border border-[#e9495a]'>
                    <WarningAmberIcon className='text-primary' />
                    <p className='text-primary font-bold text-lg'>{t('referral:referral_disabled_title')}</p>
                    <p className='text-dark text-sm'>{t('referral:referral_disabled_subtitle')}</p>
                </div>
            ) : (
                <>
                    <div className='w-full flex flex-col items-center justify-center'>
                        <div className='flex items-center justify-center'>
                            <p className='text-primary font-bold text-xl md:text-2xl'>10 {t('referral:referral_credits_text')}</p>
                        </div>
                        <p className='text-dark font-bold text-md md:text-lg text-center'>{t('referral:header_subtitle')}</p>
                    </div>

                    <div className='w-full flex flex-col items-center justify-center'>
                        <p className='text-dark font-regular text-sm md:text-md'>{t('referral:referral_helper_text')}</p>
                    </div>
                    <div className='w-full flex flex-col items-center justify-center gap-3'>
                        <TextField
                            variant="outlined"
                            fullWidth
                            className='w-full'
                            value={data.referral_code}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <IconButton onClick={handleCopy} size="small">
                                            <ContentCopyIcon />
                                        </IconButton>
                                    ),
                                    readOnly: true
                                }
                            }}
                        />
                        <Button variant="contained" className='w-full bg-primary hover:bg-primary-hover' onClick={handleShare}>
                            {t('referral:referral_modal_button_share')}
                        </Button>
                    </div>
                </>
            )}

            <div className='w-full'>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className='w-full'
                    TabIndicatorProps={{
                        style: {
                            backgroundColor: '#E9495A',
                        },
                    }}
                >
                    <Tab label={`${t('referral:table_pending_text')} (${pendingCount})`} sx={{
                        '&.Mui-selected': {
                            color: '#E9495A',
                        },
                    }} />
                    <Tab label={`${t('referral:table_expired_text')} (${rejectedCount})`} sx={{
                        '&.Mui-selected': {
                            color: '#E9495A',
                        },
                    }} />
                    <Tab label={`${t('referral:table_completed_text')} (${completedCredits} C)`} sx={{
                        '&.Mui-selected': {
                            color: '#E9495A',
                        },
                    }} />
                </Tabs>

                <div className='bg-white rounded-sm shadow-md mt-1'>
                    <div className='w-full'>
                        {loading ? (
                            <div className='w-full h-auto flex items-center justify-center m-auto p-8'>
                                <img src={Loader} alt='Loading' style={{ width: '3rem', height: 'auto', objectFit: 'cover' }} />
                            </div>
                        ) : !loading && tableData.length > 0 ? (
                            tableData.map((item: ReferralItem) => {
                                const { date, time } = formatDateAndTime(item.date);
                                const statusText = getStatusText(item.status);
                                const fullName = `${item.referred_user.first_name} ${item.referred_user.last_name}`;
                                const credits = item.status === ReferralStatus.ACCEPTED ? 10 : 0;

                                return (
                                    <div key={item.id} className='flex flex-row items-center p-4 w-full justify-between'>
                                        <div className='flex flex-col items-start'>
                                            <div className='flex-1 font-medium'>{fullName}</div>
                                            <div className='flex-1 font-regular text-sm text-text-tertiary'>
                                                {statusText} {t('referral:referral_on')} {date}, {time}
                                            </div>
                                        </div>
                                        <div className='flex justify-end items-center min-w-[60px]'>
                                            <span className='text-primary font-bold text-md'>{credits > 0 ? `+${credits}C` : ''}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : !loading && tableData.length === 0 ? (
                            <div className='flex flex-col items-center justify-center p-8'>
                                <p className='text-text-tertiary text-sm text-center'>
                                    {activeTab === 0 ? t('referral:referral_no_pending_text') :
                                        activeTab === 1 ? t('referral:referral_no_expired_text') : t('referral:referral_no_completed_text')}
                                </p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <ModalReferralCode
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                link={promoCode}
                image=""
            />
        </div>
    )
}

export default Referral;